import React, { MouseEvent, useEffect, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Hyperlink, VRM } from '@motorway/mw-highway-code/alpha';
import { BUTTON_AS_TYPE, TYPOGRAPHY, VARIANT } from '@motorway/mw-highway-code/enums';

import cypressIds from 'CypressId';

import { applyCypressData } from 'Utilities/index';
import { VehicleLookupState } from 'Utilities/vehicles/@types';

import LocalTexts from '../../../homepage/HomepageTexts.json';
import useShowNewHomepage from '../../../homepage/Hooks/useShowNewHomepage';

import styles from './HeroVehicle.scss';

const LocalT = new MDText(LocalTexts);
const t = (key: string, args?: Record<string, string>) => `${LocalT.translate(key, args)}`;

type HeroVehicleProps = {
  className?: string;
  isCompactDesign?: boolean;
  onNotMyCarClickHandler: (value: string) => void,
  vehicle: VehicleLookupState;
};

const titleSizeClassesByLength = [
  [[0, 32], undefined],
  [[32, 40], styles.md],
  [[40, 48], styles.sm],
  [[48, Infinity], styles.xs],
] as const;

const getTitleSizeClass = (title:string) => {
  const { length } = title;
  return titleSizeClassesByLength.find(([[minLength, maxLength]]) => length >= minLength && length < maxLength)?.[1];
};

export const HeroVehicle = ({
  className,
  isCompactDesign = false,
  onNotMyCarClickHandler,
  vehicle,
}: HeroVehicleProps) => {
  const [loaded, setLoaded] = useState(false);

  const { showNewHomepage } = useShowNewHomepage();

  useEffect(() => {
    setLoaded(true);
  }, []);

  if (!vehicle) {
    return null;
  }

  const manufactureYear = vehicle.manufactureYear?.toString() ?? vehicle.year?.toString();
  const { body, colour, fuel } = vehicle ?? {};

  const vehicleMakeAndModel = `${vehicle.make?.display_name} ${vehicle.model}`;

  return (
    <div className={cx(styles.component, className)}>
      <div className={cx(styles.vrmBadgeContainer, {
        [styles.show]: loaded,
        [styles.variant]: isCompactDesign,
        [styles.nextHomepage]: showNewHomepage,
      })}>
        <VRM
          data-thc-badge-small
          id={vehicle.vrm_pretty}
          side= {'front'}
          size={TYPOGRAPHY.SMALL}
          text={vehicle.vrm_pretty as string}
        />
        <VRM
          data-thc-badge-regular
          id={vehicle.vrm_pretty}
          side= {'front'}
          size={TYPOGRAPHY.REGULAR}
          text={vehicle.vrm_pretty as string}
        />
      </div>

      <h1
        className={cx(
          styles.vehicleTitle,
          { [styles.variant]: isCompactDesign, [styles.nextHomepage]: showNewHomepage },
          isCompactDesign && getTitleSizeClass(vehicleMakeAndModel),
        )}
        {...applyCypressData(cypressIds.strings.vehicleMakeAndModel)}
      >
        {vehicleMakeAndModel}
      </h1>
      <ul
        className={cx(styles.vehicleDetails, {
          [styles.variant]: isCompactDesign,
          [styles.nextHomepage]: showNewHomepage,
        })}
        {...applyCypressData(cypressIds.strings.vehicleSpecifics)}>
        { manufactureYear && <li>{ manufactureYear }</li>}
        { colour && <li>{ colour }</li>}
        { body && <li>{ body }</li>}
        {fuel && <li>{fuel}</li>}
        <li>
          <Hyperlink
            data-thc-hyperlink
            as={ BUTTON_AS_TYPE.ANCHOR }
            data-cy={ 'notMyCar' }
            data-testid={'notYourCar'}
            href={ '/' }
            label={ t('hero.mileageStep.notYourCar') }
            onClick={ (e: MouseEvent) => {
              e.preventDefault();
              onNotMyCarClickHandler(vehicle.vrm ?? '');
            } }
            variant={ VARIANT.SECONDARY }
          />
        </li>
      </ul>
    </div>
  );
};
